import { register } from "serviceWorkerRegistration";
import * as Sentry from "@sentry/react";

import { createContext, useEffect, useMemo, useState } from "react";

export type UpdateTarget = "OFFLINE_SCANS" | "TICKETS" | "SCANS" | "ALL";

export type UpdateResult = {
	offlineScansSent?: number;
	newTickets?: number;
	newScans?: number;
	busy?: boolean;
	error?: { name: string; message: string; stack?: string };
};

interface ServiceWorkerContextType {
	// Can offload work to the SW right now? False on first install, when SW is
	// unsupported, or when no controller has claimed this page yet.
	available: boolean;
	// A new SW is installed and waiting — show the "reload?" prompt.
	updateAvailable: boolean;
	// One-shot request/reply against the SW. Rejects on timeout / no controller /
	// postMessage failure; the caller is expected to fall back to main-thread sync.
	swUpdate: (target: UpdateTarget, apikey: string, device_name?: string) => Promise<UpdateResult>;
	// Manually check for a new SW build.
	update: () => Promise<void>;
	// Activate the waiting SW and reload onto the new build.
	skipWaitingAndReload: () => Promise<void>;
}

const SW_CALL_TIMEOUT_MS = 30_000;

const swSupported = typeof navigator !== "undefined" && "serviceWorker" in navigator;

const getRegistration = (): Promise<ServiceWorkerRegistration | undefined> => {
	if (!swSupported) return Promise.resolve(undefined);
	return navigator.serviceWorker.getRegistration("/").catch(e => {
		Sentry.captureException(e);
		return undefined;
	});
};

const update = () =>
	getRegistration().then(r => {
		r?.update().catch(e => Sentry.captureException(e));
	});

const skipWaitingAndReload = async () => {
	try {
		const r = await getRegistration();
		// Cover both states the new worker can be in by the time the user clicks.
		const waitingWorker = r?.waiting ?? r?.installing ?? null;
		waitingWorker?.postMessage({ type: "SKIP_WAITING" });
	} catch (e) {
		Sentry.captureException(e);
	} finally {
		window.location.reload();
	}
};

// Single-shot request to the active SW over a fresh MessageChannel. A timeout
// is the unified failure signal for "no controller" / "wrong release" /
// "wedged sync" — the caller falls back to main-thread sync on any rejection.
const callSW = <T,>(type: string, data: Record<string, any> = {}, timeoutMs = SW_CALL_TIMEOUT_MS): Promise<T> => {
	const ctl = swSupported ? navigator.serviceWorker.controller : null;
	if (!ctl) return Promise.reject(new Error("No service worker controller"));
	return new Promise<T>((resolve, reject) => {
		const ch = new MessageChannel();
		const cleanup = () => {
			ch.port1.onmessage = null;
			ch.port1.close();
		};
		const timer = setTimeout(() => {
			cleanup();
			reject(new Error(`SW '${type}' timed out after ${timeoutMs}ms`));
		}, timeoutMs);
		ch.port1.onmessage = (event: MessageEvent) => {
			clearTimeout(timer);
			cleanup();
			resolve(event.data);
		};
		try {
			ctl.postMessage({ type, ...data }, [ch.port2]);
		} catch (e) {
			clearTimeout(timer);
			cleanup();
			reject(e);
		}
	});
};

const swUpdate = (target: UpdateTarget, apikey: string, device_name?: string): Promise<UpdateResult> =>
	callSW<UpdateResult>("UPDATE", { target, apikey, device_name });

export const ServiceWorkerContext = createContext<ServiceWorkerContextType>({
	available: false,
	updateAvailable: false,
	swUpdate,
	update,
	skipWaitingAndReload,
});

// Kick off registration at module load — once per session, no React lifecycle dependency.
register().catch(e => Sentry.captureException(e));

const ServiceWorkerContextProvider = ({ children }) => {
	const [available, setAvailable] = useState<boolean>(swSupported && navigator.serviceWorker.controller != null);
	const [updateAvailable, setUpdateAvailable] = useState(false);

	useEffect(() => {
		if (!swSupported) return;
		let cancelled = false;
		let regCleanup: (() => void) | undefined;

		const refreshAvailable = () => setAvailable(navigator.serviceWorker.controller != null);

		// `ready` resolves once *some* SW is active. The registering tab may still
		// be uncontrolled (no clients.claim) — that's normal on first install and
		// is reflected by `available = false` until the next page load.
		navigator.serviceWorker.ready
			.then(reg => {
				if (cancelled) return;

				const checkWaiting = () => {
					if (reg.waiting && navigator.serviceWorker.controller) {
						setUpdateAvailable(true);
					}
				};

				const onUpdateFound = () => {
					const inst = reg.installing;
					if (!inst) return;
					const onState = () => {
						if (inst.state === "installed" && navigator.serviceWorker.controller) {
							// New SW is installed but old one is still controlling — show prompt.
							setUpdateAvailable(true);
						} else if (inst.state === "redundant") {
							const err = new Error("Service worker became redundant during install");
							console.error(err.message);
							Sentry.captureException(err);
						}
					};
					inst.addEventListener("statechange", onState);
				};

				checkWaiting();
				reg.addEventListener("updatefound", onUpdateFound);
				regCleanup = () => reg.removeEventListener("updatefound", onUpdateFound);
			})
			.catch(e => Sentry.captureException(e));

		navigator.serviceWorker.addEventListener("controllerchange", refreshAvailable);
		refreshAvailable();

		return () => {
			cancelled = true;
			navigator.serviceWorker.removeEventListener("controllerchange", refreshAvailable);
			regCleanup?.();
		};
	}, []);

	const ctx = useMemo(
		() => ({
			available,
			updateAvailable,
			swUpdate,
			update,
			skipWaitingAndReload,
		}),
		[available, updateAvailable],
	);

	return <ServiceWorkerContext.Provider value={ctx}>{children || null}</ServiceWorkerContext.Provider>;
};

export default ServiceWorkerContextProvider;
